import React from 'react';
import Page from "../Page";
import {Img} from "react-image";
import JobMatchForm from "./components/JobMatchForm";
import TalentPortal from '../talent-portal/TalentPortal';
import {DisciplineSkillsStep} from "../talent-application/steps";
import LoadingSpinner from "../../components/LoadingSpinner";
import JobReferralModal from "./components/JobReferralModal";
import ProjectPosition from "../../models/ProjectPosition";
import Opportunity from "../../models/Opportunity";
import {TalentOppQuickSummary} from "../talent-opportunities/OppQuickSummary";
import {OppProposalModal} from "../proposals/OppProposalWalkthrough";
import {generatePath, Link, Navigate, useLocation, useNavigate, useParams} from "react-router-dom";
import ResourceNotFound from "../../components/errors/ResourceNotFound";
import PageNotAllowed from '../../components/errors/PageNotAllowed';
import OpportunityProvider from "../../providers/OpportunityProvider";
import {useAuth} from "../../providers/AuthProvider";
import {List} from "antd";


const JobDescription = ({job, ...props}) => {

    return (
        <React.Fragment>
            <Img src={[
                job.client?.banner_image_path,
                TB.defaultClientBannerImage()
            ]} className="job-client-banner img-fluid"/>

            <div className="project-details">
                <h4 className="mb-1.5">Project: {job.project.name}</h4>
                {(job.project.description || job.description) ? (
                    <div className="rich-text-display">
                        <span
                            dangerouslySetInnerHTML={{__html: job.project.description || job.description}}></span>
                    </div>
                ) : (
                    <div className="rich-text-display">
                        <span>
                            <p className="italic">No description provided</p>
                        </span>
                    </div>
                )}
                <p className="text-base my-3"><span className="font-semibold">{job.title}</span></p>
                <p className="text-xs mb-1.5">
                    <span
                        className="text-wripplePrimaryDarkNavy font-semibold">Wripple {TB.pluralize('Role', job.disciplines?.length)}:
                    </span>
                    &nbsp;{TB.map(job.disciplines || [], (dis, i) => (
                    <React.Fragment key={`${dis.name}-${job.id}`}>
                        <span className="text-primary font-semibold">
                            {dis.name}
                        </span>
                        {i < job.disciplines?.length - 1 && ', '}
                    </React.Fragment>
                ))}
                </p>

                {job.isFixedFee() && job.deliverables && (
                    <div className="flex flex-col items-start mt-1.5 gap-2">
                        <p className="text-xs">
                            <span className="text-wripplePrimaryDarkNavy font-semibold">
                                Requested Deliverables:
                            </span>
                        </p>
                        <List
                            size="large"
                            dataSource={job.deliverables}
                            renderItem={(deliverable) => (
                                <List.Item className="flex-col">
                                    <div key={deliverable.id} className="flex flex-col gap-1.5 self-stretch items-stretch">
                                        <p className="text-base">
                                            <span className="font-medium text-primary">
                                                {deliverable.name}
                                            </span>
                                        </p>
                                        <p className="text-xs mb-3.5">
                                            {deliverable.description || <i>No Description Provider</i>}
                                        </p>
                                        <div className="flex flex-row items-center justify-start">
                                            <p className="text-xs min-w-[220px] max-w-[2200px]">
                                                <span className="font-medium">Estimated # of hours:</span>
                                                &nbsp;
                                                {deliverable.total_hours}
                                            </p>

                                            <p className="text-xs min-w-[220px] max-w-[2200px]">
                                                <span className="font-medium">Target Delivery Date:</span>
                                                &nbsp;
                                                {deliverable.target_delivery_at ? TB.moment(deliverable.target_delivery_at)
                                                    .format('ll') : (
                                                    <i>N/A</i>
                                                )}
                                            </p>

                                        </div>
                                    </div>
                                </List.Item>
                            )}/>

                    </div>
                )}
            </div>

        </React.Fragment>
    )
}
export default class JobDetails extends Page {

    static path = '/job-board/job-details/:jobId'

    static pathForJob(job) {
        const user = TB.auth().user();

        let pathPrefix = '/talent'
        if (user && user.canManageClientResourceTalent()) {
            pathPrefix = '/supplier'
        }

        return pathPrefix + generatePath(this.path, {jobId: job.id})
    }

    constructor(props, context) {
        super(props, context);

        this.state = {
            viewFullDescription: false,
            error: false,
            isLoading: true
        }
    }

    async componentDidMount() {
        super.componentDidMount();

        await TB.wait(350);

        if (!TB.has(this.props, 'jobId') || TB.isNaN(parseInt(this.props.jobId))) {
            return this.props.navigate(TB.auth().appropriate())
        }

        let auth = this.context?.auth
        let job;

        if (auth.canManageClientResourceTalent()) {
            job = await TB.auth()?.fetchJobDetailsForTalent(this.props.jobId)
        } else {
            job = await auth?.updateForProjectPositionMatching(this.props.jobId, {})
        }

        if (!job) {
            await this.promisedState({isLoading: false, error: true})


            // await TB.showServerErrorToast();
            return;
        }

        return this.promisedState({job, isLoading: false})
    }

    onUpdateJob = (job) => {
        return this.promisedState({
            job: ProjectPosition.make({
                ...this.state.job,
                ...job,
                posted_at: this.state.job.posted_at,
                location: this.state.job.location,
                client: this.state.job.client
            })
        })
    }

    onToggleInterest = async (interestForJob = this.state.job) => {
        let response;
        if (interestForJob.has_shown_interest_in_job) {
            let confirm = await TB.alert('warning', 'Withdraw Interest?',
                'Are you sure you want to withdraw your interest for this job? Access to client and proposal will be lost',
                [{title: 'Yes, Continue'}, {title: 'Cancel'}]
            );
            if (!confirm) {
                return
            }
            response = await this.onRequest(() => this.context?.auth.toggleInterestForJob(interestForJob))

        }
        else {
            response = await this.onRequest(() => this.context?.auth.proposeInterestForJob(interestForJob))
        }
        if (!response) {
            return TB.showServerErrorToast();
        }
        if (response instanceof ProjectPosition) {
            await this.onUpdateJob(response)
            await TB.event("Modal@hide")
            await TB.wait(300);
            await TB.toast('Interest Withdrawn for Job');
        } else if (response instanceof Opportunity) {
            // await this.onUpdateJob(response.relatedJob())

            await this.promisedState({opportunity: response});

            await TB.sysAlert("talent.propose_interest_and_begin_proposal", undefined, {
                icon: "fas fa-thumbs-up"
            });

            return this.props.navigate({...this.props.location, search: 'proposal'})
        }
    }
    onReferJob = () => {
        TB.event("Modal@show", {
            modalClass: "job-referral-modal-form",
            content: (
                <JobReferralModal
                    job={this.state.job}
                    user={this.context?.auth}
                    onRequestClose={() => TB.event("Modal@hide")}
                    disabled={this.state.isDisabled || this.state.isLoading}
                    onRequest={this.onRequest.bind(this)}
                    onSuccess={async () => {
                        await TB.event("Modal@hide")
                        await TB.wait(300);
                        await TB.sysAlert('talent.job_referral_submitted_success');
                    }}
                />
            )
        })
    }

    renderClientSideBar() {
        let {
            job
        } = this.state;

        return (
            <TalentOppQuickSummary
                job={job} isDisabled={this.state.isDisabled}
                onToggleInterest={this.onToggleInterest.bind(this)}
                onReferJob={this.onReferJob.bind(this)}
            />
        )
    }

    async onRequest(callback) {
        await this.promisedState({isDisabled: true});

        let response = await callback();

        await this.promisedState({isDisabled: false})

        return response
    }

    renderJobMatch() {
        let {
            job
        } = this.state;

        return (
            <div className="job-details-candidate-score">

                <JobDescription job={job}/>
                <hr/>

                <div className="candidate-scoring">
                    {(job.is_match && job.matching) ? (
                        <div className="job-match-score-panel">
                            <div className="job-match-header">
                                <p>My Match Score for this Role</p>
                            </div>
                            <p className="pt-3">
                                Please review the client’s requirements compared to what’s in your profile. If you
                                have
                                experience in missing areas (in gray), you can add them to your profile here.
                            </p>

                            <JobMatchForm
                                {...this.props}
                                key={`job-match-${job?.matching?.overall?.percent}`}
                                onRequest={this.onRequest.bind(this)}
                                disabled={this.state.isDisabled}
                                onUpdateJob={updatedJob => this.setState({updatedJob})}
                                job={job}/>
                        </div>
                    ) : (
                        <div className="job-match-score-panel no-match">
                            <div className="job-match-header">
                                <p>
                                    Match Score
                                </p>
                            </div>
                            <p className="pt-3 no-match-header">
                                {TB.tryTrans('talent.job_details.no_role_match_header')}
                            </p>
                            <p className="pt-3 no-match-info">
                                {TB.tryTrans('talent.job_details.no_role_match_info')}
                            </p>

                            <Link to={DisciplineSkillsStep.getPath()}
                                  className="btn btn-primary no-match-btn">
                                {TB.tryTrans('talent.job_details.no_role_match_button')}
                            </Link>

                        </div>
                    )}

                </div>
            </div>

        )
    }

    getBackBtnLink() {
        let redirect = TalentPortal.jobBoardPath();
        if (!TB.auth().InitIntended?.pathname.includes(`/job-details/${this.props.jobId}`)) {
            redirect = -1;
        }
        return redirect;
    }


    render() {
        if (this.context.auth?.isTalentSupplierResource()) {
            return <PageNotAllowed redirect="/talent/dashboard"/>
        }

        if (this.state.error) {
            return <ResourceNotFound type="Job" exitLink={this.getBackBtnLink()} exitText="Return to Job Board"/>
        }
        return (
            <div className="job-details-page-container container">

                <button onClick={() => this.props.navigate(this.getBackBtnLink())}
                        className="btn btn-link btn-link-dark">
                    <i className="fas fa-arrow-left"/>&nbsp;{this.props.location.state?.label || 'Back to Job Board'}
                </button>

                <div className="job-details-page row flex-col sm:flex-row-reverse md:flex-row">
                    {this.state.isLoading && (
                        <div className="w-full job-match-loading">
                            <LoadingSpinner size={'lg'}/>
                        </div>
                    )}
                    {!this.state.isLoading && this.state.job && (
                        <React.Fragment>
                            <div className="col sm:w-full md:w-9/12">
                                {this.renderJobMatch()}
                            </div>
                            <div className="col sm:w-full md:w-3/12">
                                {this.renderClientSideBar()}
                            </div>
                        </React.Fragment>
                    )}
                </div>

                {this.state.opportunity && (
                    <OpportunityProvider model={this.state.opportunity} onReset={async (opp) => {
                        await this.onUpdateJob(opp.relatedJob())
                        return this.setState({opportunity: opp})
                    }}>
                        <OppProposalModal onClose={async (updatedJob) => {
                            if (updatedJob) {
                                await this.onUpdateJob(updatedJob);
                            }
                            return this.setState({opportunity: null});
                        }}/>
                    </OpportunityProvider>
                )}
            </div>
        );
    }
}

const JobDetailPage = (props) => {

    const navigate = useNavigate();
    const location = useLocation();
    const {jobId} = useParams();
    const {auth} = useAuth();

    if (!jobId) {
        return <ResourceNotFound type="Job" exitLink={'/talent/job-board'} exitText={"Back to Job Board"}/>
    }
    if (auth?.isTalentSupplierResource()) {
        return <Navigate to={'/talent/dashboard'} replace={true}/>
    }
    if (auth.isTalent() && !auth.isProfileApproved()) {
        return <PageNotAllowed redirect="/talent/dashboard" message="Your profile must be approved before accessing this page."/>;
    }
    return (
        <JobDetails navigate={navigate} location={location} jobId={jobId}/>
    )
}


const JobDetailRedirect = (props) => {

    const navigate = useNavigate();
    const location = useLocation();
    const {jobId} = useParams();
    const {auth} = useAuth();
    if (auth?.isTalentSupplierResource()) {
        return <Navigate to={'/talent/dashboard'} replace={true}/>
    }
    return (
        <Navigate to={`/talent/job-board/job-details/${jobId}`} replace={true}/>
    )
}


export {
    JobDescription,
    JobDetailPage,
    JobDetailRedirect
}